import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { FormField } from '@/shared/ui/FormField';
import { ApiError } from '@/shared/api/client';
import { phoneLogin, pollOtp, startOtp } from './api';
import type { LoginResponseLike } from '@/shared/auth/storage';

interface Props {
  onLoggedIn: (res: LoginResponseLike) => void;
}

type Stage = 'phone' | 'code';

function fmtCountdown(s: number): string {
  const m = Math.floor(s / 60);
  return `(${m}:${String(s % 60).padStart(2, '0')})`;
}

export function OtpStep({ onLoggedIn }: Props) {
  const [stage, setStage] = useState<Stage>('phone');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [destNumber, setDestNumber] = useState('');
  const [instruction, setInstruction] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const sessionIdRef = useRef<string | null>(null);
  const expiresAtRef = useRef<number>(0);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  function clearTimers() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  useEffect(() => () => clearTimers(), []);

  function reset(reason: string | null) {
    clearTimers();
    sessionIdRef.current = null;
    setStage('phone');
    setBusy(false);
    if (reason) setError(reason);
  }

  async function onSubmitPhone(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{8}$/.test(phone)) {
      setError('Утасны дугаар буруу байна (8 оронтой).');
      return;
    }
    setBusy(true);
    try {
      const r = await startOtp(phone);
      sessionIdRef.current = r.SessionId;
      expiresAtRef.current = new Date(r.ExpiresAt).getTime();
      const instr = r.DisplayInstruction ?? '';
      const code4 = instr.match(/\b(\d{4})\b/);
      const dest = instr.match(/\b(\d{5,7})\b/);
      setSmsCode(code4?.[1] ?? '');
      setDestNumber(dest?.[1] ?? '');
      setInstruction(code4 ? '' : instr);
      setStage('code');
      tickRef.current = window.setInterval(() => {
        const left = Math.max(0, Math.floor((expiresAtRef.current - Date.now()) / 1000));
        setSecondsLeft(left);
      }, 1000);
      pollRef.current = window.setInterval(async () => {
        const id = sessionIdRef.current;
        if (!id) return;
        try {
          const s = await pollOtp(id);
          if (s.SessionStatus === 'VERIFIED') {
            clearTimers();
            const login = await phoneLogin(phone, id);
            onLoggedIn(login);
          } else if (s.SessionStatus === 'EXPIRED' || s.SessionStatus === 'FAILED') {
            reset('Хугацаа дууссан. Дахин оролдоно уу.');
          }
        } catch (err) {
          if (err instanceof ApiError && err.status >= 500) {
            // graceful: үргэлжлүүлэн polling.
          }
        }
      }, 1000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Сервертэй холбогдож чадсангүй.');
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'phone') {
    return (
      <form onSubmit={onSubmitPhone}>
        <FormField
          label="Утасны дугаар"
          name="phone"
          inputMode="numeric"
          maxLength={8}
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value.replace(/\D/g, ''))}
          required
          error={error}
          hint="8 оронтой"
        />
        <Button type="submit" disabled={busy} icon="send" style={{ marginTop: 14, width: '100%' }}>
          {busy ? 'Илгээж байна…' : 'SMS код илгээх'}
        </Button>
      </form>
    );
  }

  return (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>{phone}</strong> руу илгээсэн SMS-ийн доорх код-ыг
        {destNumber ? <> <strong>{destNumber}</strong> руу илгээнэ үү.</> : <> хүлээнэ үү.</>}
      </p>
      {smsCode ? (
        <div style={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '0.2em', textAlign: 'center', margin: '18px 0' }}>
          {smsCode}
        </div>
      ) : null}
      {instruction ? (
        <div style={{ color: 'var(--ts-muted)', fontSize: '0.85rem' }}>{instruction}</div>
      ) : null}
      <div style={{ color: 'var(--ts-muted)', fontSize: '0.85rem', marginTop: 8 }}>
        Хугацаа {fmtCountdown(secondsLeft)}
      </div>
      {error ? (
        <div style={{ color: '#ef4444', marginTop: 10 }}>{error}</div>
      ) : null}
      <Button
        variant="outline"
        onClick={() => reset(null)}
        style={{ marginTop: 14, width: '100%' }}
      >
        Цуцлах
      </Button>
    </div>
  );
}
